import { ExtranetServiceDescriptionRouteDto } from '@pharmac-extranet/extranet-api'
import { Location, PathMatch, matchPath } from 'react-router-dom'
import { NavigationLink } from '../../components/navigation/useExtranetLinks'
import { ExtranetRouteObject } from '../../router/ExtranetRouter'
import { routesEn, routesFi, routesSv } from '../../router/i18n/extranet-routes'

export const getRoutesForLanguage = (lang: string) => {
  switch (lang) {
    default:
    case 'fi':
      return routesFi
    case 'sv':
      return routesSv
    case 'en':
      return routesEn
  }
}

const isServiceMeta = (s: unknown): s is ExtranetServiceDescriptionRouteDto => {
  if (!s) return false
  const m = s as ExtranetServiceDescriptionRouteDto

  return typeof m.path === 'object'
}

const metaHasPath = (linkMeta: NavigationLink['meta'], path?: string) => {
  return isServiceMeta(linkMeta) && !!path && Object.values(linkMeta.path).includes(path)
}

const metaHasPathStatic = (linkMeta: NavigationLink['meta'], path?: string) => {
  return (
    isServiceMeta(linkMeta) &&
    !!path &&
    Object.values(linkMeta.path).some((a) => matchPath(a, path))
  )
}

export const isValidLanguage = (s: unknown): s is 'fi' | 'en' | 'sv' =>
  s === 'fi' || s === 'sv' || s === 'en'

export const matchExtranetRoutes = (currentLanguage: string, location: Location<any>) => {
  const pathname = location.pathname.replace(/^\/(en|sv)\/?/, '/')

  return Object.values(getRoutesForLanguage(currentLanguage)).reduce<PathMatch<string>[]>(
    (acc, path) => {
      const match = matchPath(path, pathname)

      return !!match ? acc.concat(match) : acc
    },
    []
  )
}

export const matchExtranetRoute = (matches: PathMatch<string>[]): PathMatch<string> => {
  const nonWildcardMatch = matches.find((a) => a.pattern.path !== '*')

  return nonWildcardMatch ?? matches[0]
}

export const hasSlug = (match: PathMatch<string>): boolean => {
  return match.pattern.path.includes('slug')
}

const findRouteWithSlug = (slug: string | undefined, link: NavigationLink) => {
  const matchSlug = (
    slug?: string,
    meta?: ExtranetServiceDescriptionRouteDto | ExtranetRouteObject
  ) => {
    return !!slug && isServiceMeta(meta) && metaHasPath(meta, slug)
  }

  const reduceMatch = (acc: false | NavigationLink, link: NavigationLink) => {
    if (isServiceMeta(link.meta) && matchSlug(slug, link.meta)) return link

    if (link.children && link.children.length > 0) {
      const match = link.children.reduce<false | NavigationLink>(reduceMatch, false)

      if (match) return match
    }

    return acc
  }

  if (isServiceMeta(link.meta) && matchSlug(slug, link.meta)) return link

  if (link.children && link.children.length > 0) {
    const match = link.children.reduce(reduceMatch, false)

    if (match) return match
  }

  return false
}

const findDynamicRoute = (
  match: PathMatch<string>,
  extranetLinks: NavigationLink[]
): NavigationLink | false =>
  extranetLinks.reduce<NavigationLink | false>((acc, curr) => {
    const matchFound = findRouteWithSlug(match.params?.slug, curr)

    return matchFound || acc
  }, false)

const findStaticRoute = (
  match: PathMatch<string>,
  extranetLinks: NavigationLink[],
  language: string
): NavigationLink | false => {
  const path = match.pathname === '/' && language !== 'fi' ? '/' + language : match.pathname

  const reduceMatch = (acc: false | NavigationLink, link: NavigationLink) => {
    if (acc !== false) return acc

    if (link.path === path) return link

    if (isServiceMeta(link.meta) && metaHasPathStatic(link.meta, path)) {
      return link
    }

    if (link.children && link.children.length > 0) {
      const matchingLink = link.children.reduce<false | NavigationLink>(reduceMatch, false)

      if (matchingLink) return matchingLink
    }

    return acc
  }

  return extranetLinks.reduce<NavigationLink | false>(reduceMatch, false)
}

export const findMatchForNextLanguage = (
  match: PathMatch<string>,
  extranetLinks: NavigationLink[],
  i18nLanguage: string
) => {
  if (hasSlug(match)) {
    const matchingRoute = findDynamicRoute(match, extranetLinks)

    if (matchingRoute) return matchingRoute
  }

  const staticMatch = findStaticRoute(match, extranetLinks, i18nLanguage)

  return staticMatch
}
